/**
 * Mission Queue — pure cross-sentinel fusion/ranking/dedupe engine.
 *
 * Every scattered sentinel (deal, margin, project, health, approvals…)
 * emits mission items; this module fuses them into ONE ranked daily
 * decision stream for the Today card stack. Radically convergent by
 * construction:
 *
 *   • scoreMissionItem — $-at-stake × urgency × confidence, tier-weighted
 *     so proposals that need a human ('ask'/'block'-style tiers) outrank
 *     equal-dollar items an agent could have executed itself.
 *   • dedupeMission    — two sentinels shouting about the SAME account +
 *     overlapping intent collapse into one card (evidence unioned,
 *     mergedFrom recording every fused source id).
 *   • buildMissionQueue — dedupe then stable sort desc by score.
 *   • missionZeroState — the celebratory empty state, fed by the caller's
 *     honest rollup (handled count, dollars influenced, reversals).
 *
 * Deliberately PURE: no store, no agent I/O, no clock. All inputs are
 * passed in; every function returns new data and never mutates its input.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** One decision-ready card in the fused daily stream. */
export type MissionItem = {
  /** Unique id for the card (survives merging via the winner's id). */
  id: string;
  /** Which sentinel/agent produced this (e.g. 'dealSentinel'). */
  sourceAgent: string;
  /** Account the item concerns — dedupe only fuses within an account. */
  accountId?: string;
  /** Human-readable headline for the card. */
  title: string;
  /** Dollars this decision genuinely touches (influenced, never banked). */
  dollarsAtStake: number;
  /** 0..1 — how time-critical the decision is. */
  urgency: number;
  /** 0..1 — how sure the proposing agent is. */
  confidence: number;
  /** Autonomy tier: 0 = would auto-run, higher = needs a human more. */
  autonomyTier: number;
  /** Reference to the underlying proposal (approvals id, etc.). */
  proposalRef: string;
  /** Click-verifiable "why" lines backing the card. */
  evidence: string[];
  /** Present after a merge: ids of every source item fused into this card. */
  mergedFrom?: string[];
};

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/** Clamp into [0, 1]; non-finite collapses to 0. */
function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/**
 * Rank key for one card: dollars-at-stake × urgency × confidence, weighted
 * by autonomy tier (weight = 1 + tier) — the more a proposal needs a human,
 * the higher equal-dollar work floats. Negative dollars clamp to 0 so a
 * malformed item never outranks real money; urgency/confidence clamp to
 * [0, 1]. Pure and deterministic.
 */
export function scoreMissionItem(i: MissionItem): number {
  const dollars = Number.isFinite(i.dollarsAtStake) ? Math.max(0, i.dollarsAtStake) : 0;
  const tier = Number.isFinite(i.autonomyTier) ? Math.max(0, i.autonomyTier) : 0;
  return dollars * clamp01(i.urgency) * clamp01(i.confidence) * (1 + tier);
}

// ---------------------------------------------------------------------------
// Dedupe (fusion)
// ---------------------------------------------------------------------------

/** Significant lowercase tokens of a title — the card's "intent" signature. */
function intentTokens(title: string): Set<string> {
  const tokens = title
    .toLowerCase()
    .split(/[^a-z0-9$]+/)
    .filter((t) => t.length >= 3);
  return new Set(tokens);
}

/** Two items overlap in intent when they cite the same proposal or their
 *  title signatures share at least one significant token. */
function intentOverlaps(a: MissionItem, b: MissionItem): boolean {
  if (a.proposalRef && a.proposalRef === b.proposalRef) return true;
  const ta = intentTokens(a.title);
  for (const t of intentTokens(b.title)) {
    if (ta.has(t)) return true;
  }
  return false;
}

/** Union two string lists preserving first-seen order. */
function union(a: string[], b: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of [...a, ...b]) {
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/** Fuse a duplicate into an existing card: the higher-scored side leads
 *  (title/id/proposalRef), stakes/urgency/confidence/tier take the max so
 *  no signal is lost, evidence is unioned, mergedFrom records every id. */
function fuse(base: MissionItem, dupe: MissionItem): MissionItem {
  const lead = scoreMissionItem(dupe) > scoreMissionItem(base) ? dupe : base;
  return {
    ...lead,
    dollarsAtStake: Math.max(base.dollarsAtStake, dupe.dollarsAtStake),
    urgency: Math.max(clamp01(base.urgency), clamp01(dupe.urgency)),
    confidence: Math.max(clamp01(base.confidence), clamp01(dupe.confidence)),
    autonomyTier: Math.max(base.autonomyTier, dupe.autonomyTier),
    evidence: union(base.evidence, dupe.evidence),
    mergedFrom: union(base.mergedFrom ?? [base.id], dupe.mergedFrom ?? [dupe.id]),
  };
}

/**
 * Merge items that share the same accountId AND overlapping intent into a
 * single card. Items without an accountId never merge (nothing to anchor
 * the fusion to), and distinct-account items pass through untouched. The
 * output preserves first-occurrence order. Pure — input never mutated.
 */
export function dedupeMission(items: MissionItem[]): MissionItem[] {
  const out: MissionItem[] = [];
  for (const item of items) {
    const idx = item.accountId
      ? out.findIndex((o) => o.accountId === item.accountId && intentOverlaps(o, item))
      : -1;
    if (idx < 0) {
      out.push({ ...item, evidence: [...item.evidence] });
    } else {
      out[idx] = fuse(out[idx], item);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

/**
 * The one ranked daily decision stream: dedupe then sort descending by
 * {@link scoreMissionItem}. Stable for equal scores (ES2019+ stable-sort
 * guarantee), so equal cards keep their source order. Pure.
 */
export function buildMissionQueue(sources: MissionItem[]): MissionItem[] {
  return dedupeMission(sources).sort(
    (a, b) => scoreMissionItem(b) - scoreMissionItem(a),
  );
}

// ---------------------------------------------------------------------------
// Zero state
// ---------------------------------------------------------------------------

/**
 * Celebratory message for an empty queue — honest numbers only, fed by the
 * caller's real rollup. Mentions reversals only when there were any.
 */
export function missionZeroState(rollup: {
  handled: number;
  dollarsInfluenced: number;
  reversals: number;
}): { message: string } {
  const dollars = `$${Math.max(0, rollup.dollarsInfluenced).toLocaleString('en-US')}`;
  const base = `Mission zero — all clear! ${rollup.handled} decision${
    rollup.handled === 1 ? '' : 's'
  } handled, ${dollars} influenced`;
  const tail =
    rollup.reversals > 0
      ? `, ${rollup.reversals} reversal${rollup.reversals === 1 ? '' : 's'} to review.`
      : ', zero reversals.';
  return { message: base + tail };
}
